import { Controller } from "@hotwired/stimulus"

/**
 * Textarea Height Controller
 * Automatically adjusts textarea height based on content and provides
 * auto-grow functionality with Tailwind styling support.
 */
export default class extends Controller {
  static targets = ["textarea"]
  static values = {
    minRows: { type: Number, default: 3 },
    maxHeight: { type: Number, default: 0 }, // 0 means no max height
    debug: { type: Boolean, default: false },
    autoGrow: { type: Boolean, default: true },
    tallClass: { type: String, default: "tw-h-auto" },
    defaultClass: { type: String, default: "" }
  }

  connect() {
    if (this.debugValue) {
      console.log("Textarea height controller connected")
    }

    // Apply initial sizing
    this.applyToAllTextareas()

    // Set up resize events
    this.resizeHandler = this.handleWindowResize.bind(this)
    window.addEventListener('resize', this.resizeHandler)

    // Set up input event listeners for auto-grow
    if (this.autoGrowValue) {
      this.textareaTargets.forEach(textarea => {
        textarea.addEventListener('input', this.handleInput.bind(this))
      })
    }
  }

  disconnect() {
    window.removeEventListener('resize', this.resizeHandler)

    // Clean up input event listeners
    if (this.autoGrowValue) {
      this.textareaTargets.forEach(textarea => {
        textarea.removeEventListener('input', this.handleInput.bind(this))
      })
    }
  }

  handleWindowResize() {
    // Recalculate heights on window resize
    this.applyToAllTextareas()
  }

  handleInput(event) {
    // Adjust height on input
    const textarea = event.target
    this.adjustTextareaHeight(textarea)
  }

  applyToAllTextareas() {
    // Apply to all targeted textareas
    if (this.hasTextareaTarget) {
      this.textareaTargets.forEach(textarea => {
        this.adjustTextareaHeight(textarea)
      })
    }
  }

  adjustTextareaHeight(textarea) {
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto'

    // Calculate minimum height based on rows attribute or default
    const rows = parseInt(textarea.getAttribute('rows'), 10) || this.minRowsValue
    const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight, 10) || 24
    const paddingTop = parseInt(window.getComputedStyle(textarea).paddingTop, 10) || 8
    const paddingBottom = parseInt(window.getComputedStyle(textarea).paddingBottom, 10) || 8
    const borderTop = parseInt(window.getComputedStyle(textarea).borderTopWidth, 10) || 1
    const borderBottom = parseInt(window.getComputedStyle(textarea).borderBottomWidth, 10) || 1

    // Calculate minimum height
    const minHeight = (rows * lineHeight) + paddingTop + paddingBottom + borderTop + borderBottom

    // Get content height
    const contentHeight = textarea.scrollHeight

    // Determine new height
    let newHeight = Math.max(minHeight, contentHeight)

    // Apply max height if specified
    if (this.maxHeightValue > 0 && newHeight > this.maxHeightValue) {
      newHeight = this.maxHeightValue

      // Add the tall class for scrollable textareas
      if (this.tallClassValue) {
        textarea.classList.add(this.tallClassValue)
      }
    } else {
      // Remove tall class if not needed
      if (this.tallClassValue) {
        textarea.classList.remove(this.tallClassValue)
      }
    }

    // Apply the height
    textarea.style.height = `${newHeight}px`
  }

  // Action to manually trigger resize
  resize() {
    this.applyToAllTextareas()
  }
}